/**
 * Exact posting-writer arithmetic for a cent-denominated AUD service cost.
 * PostgreSQL numeric: rate = round(percent / 100, 6), then
 * contribution = round(cost * rate, 2), with ties away from zero.
 *
 * Decimal strings (including exponent notation) are parsed before any arithmetic.
 * Numbers use their decimal String representation; no binary cost × rate product
 * is formed. Costs are normalised to cents, as in the writer's numeric money
 * columns. Pass 'fraction' for existing 0..1 assessed rates to avoid a floating
 * point conversion to percentage units. This helper changes no rate or cap rule.
 */
export function roundSahParticipantContribution(
  cost: number | string,
  rate: number | string,
  rateUnit: 'percent' | 'fraction' = 'percent',
): number {
  const costCents = decimalScaled(cost, 2);
  const rateMillionths = decimalScaled(rate, rateUnit === 'percent' ? 4 : 6);
  // eslint-disable-next-line statura/no-floating-point-money -- BigInt product of integer cents and integer rate millionths; no floating point.
  const contributionCents = roundRatio(costCents * rateMillionths, 1_000_000n);
  if (
    contributionCents > BigInt(Number.MAX_SAFE_INTEGER) ||
    contributionCents < BigInt(Number.MIN_SAFE_INTEGER)
  ) {
    throw new RangeError('SAH contribution exceeds safe integer cents.');
  }
  return Number(contributionCents) / 100;
}

function roundRatio(numerator: bigint, denominator: bigint): bigint {
  const sign = numerator < 0n ? -1n : 1n;
  const magnitude = numerator < 0n ? -numerator : numerator;
  return sign * ((magnitude + denominator / 2n) / denominator);
}

function decimalScaled(value: number | string, scale: number): bigint {
  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(String(value).trim());
  if (!match) throw new RangeError('SAH contribution inputs must be finite decimals.');
  const fraction = match[3] ?? '';
  const exponent = Number(match[4] ?? 0);
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 1000) {
    throw new RangeError('SAH contribution decimal exponent is out of range.');
  }
  const digits = BigInt(match[2]! + fraction) * (match[1] === '-' ? -1n : 1n);
  const shift = scale + exponent - fraction.length;
  return shift >= 0 ? digits * 10n ** BigInt(shift) : roundRatio(digits, 10n ** BigInt(-shift));
}
